/**
 * The Publish dialog: where the piece goes, when, and the "still to look at" acknowledgement.
 * A modal in a portal (inherits data-theme from <html>): focus moves in, Tab is trapped, Esc closes,
 * and focus returns to the button that opened it.
 */
import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { describeError } from '../../lib/describeError'
import { errorCode, publish, publishPreview, type ChecklistItem, type PieceDetail } from '../api'
import { blockingCount, SAVE_FIRST } from './pieceHelpers'
import { canSubmit, publishButtonLabel, publishedMessage, slotDisabledReason, slotOptionText, type When } from './publishDialog'
import { btn, btnPrimary, btnSmall, errorText, focus } from './ui'

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'

type Props = {
  detail: PieceDetail
  onClose: () => void
  /** Saves what waits; false means the edits are not saved and nothing may be sent. */
  onBeforeSubmit: () => Promise<boolean>
  /** Show me / Fix on an open item (the dialog has already closed). */
  onShowItem: (item: ChecklistItem) => void
  /** The write went through: the line to show, and the page refetches. */
  onPublished: (message: string) => void
}

export function PublishDialog({ detail, onClose, onBeforeSubmit, onShowItem, onPublished }: Props) {
  const titleId = useId()
  const box = useRef<HTMLDivElement>(null)
  const [when, setWhen] = useState<When>('now')
  const [acknowledged, setAcknowledged] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const now = new Date()

  const preview = useQuery({
    queryKey: ['editorial', 'publish-preview', detail.id],
    queryFn: () => publishPreview(detail.id),
    gcTime: 0, staleTime: 0, refetchOnWindowFocus: false,
  })
  const p = preview.data
  const items = p?.checklist ?? []
  const open = blockingCount(items)
  const slotReason = p ? slotDisabledReason(p.next_slot, p.releases_on, detail.status) : null
  const effectiveWhen: When = slotReason ? 'now' : when

  // Focus in on open, back to the opener on close.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    box.current?.focus()
    return () => { if (opener && opener.isConnected) opener.focus() }
  }, [])

  const send = useMutation({
    mutationFn: async () => {
      if (!(await onBeforeSubmit())) throw new Error(SAVE_FIRST)
      const r = await publish(detail.id, { when: effectiveWhen, acknowledged })
      return 'slot_at' in r ? r.slot_at : null
    },
    onSuccess: (slotAt) => onPublished(publishedMessage(effectiveWhen, slotAt ?? p?.next_slot ?? null)),
    onError: (err) => {
      setError(describeError(err))
      if (errorCode(err) === 'unchecked') { setAcknowledged(false); void preview.refetch() }
    },
  })

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.stopPropagation(); onClose(); return }
    if (e.key !== 'Tab' || !box.current) return
    const els = Array.from(box.current.querySelectorAll<HTMLElement>(FOCUSABLE))
    if (els.length === 0) { e.preventDefault(); return }
    const first = els[0], last = els[els.length - 1]
    const active = document.activeElement
    if (e.shiftKey && (active === first || active === box.current)) { e.preventDefault(); last.focus() }
    else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus() }
  }

  const showItem = (item: ChecklistItem) => { onClose(); onShowItem(item) }
  const ready = !!p && canSubmit(open, acknowledged, effectiveWhen, p.releases_on, p.next_slot, detail.status)
  const optBase = 'flex items-start gap-3 rounded-[10px] border p-3.5'
  const optOn = 'border-amber-glow bg-ink-600 ring-1 ring-amber-glow'

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-900/60 px-4 py-12 font-ui" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div ref={box} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onKeyDown={onKeyDown}
           className="flex w-full max-w-[640px] flex-col rounded-2xl border border-ink-500 bg-ink-700 text-[14px] leading-snug text-paper shadow-xl focus:outline-none">
        <div className="flex flex-col gap-1.5 border-b border-ink-500 px-7 pb-4 pt-6">
          <span className="text-[12px] font-bold uppercase tracking-[0.08em] text-paper-faint">Publish</span>
          <h2 id={titleId} className="m-0 font-serif text-[26px] font-medium leading-tight">{detail.title ?? 'Untitled piece'}</h2>
        </div>

        <div className="flex flex-col gap-5 px-7 py-5">
          {preview.isLoading && <p role="status" className="m-0 text-paper-dim">Loading…</p>}
          {preview.isError && <p role="alert" className={errorText}>Could not load the publish details. {describeError(preview.error)}</p>}
          {p && (
            <>
              <section className="flex flex-col gap-2.5">
                <h3 className="m-0 text-[15px]">Where it goes</h3>
                <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(min(240px,100%),1fr))]">
                  {p.where.map((w) => (
                    <div key={w.audience} className="flex flex-col gap-1 rounded-[10px] bg-ink-600 p-3.5">
                      <span className="self-start rounded-full bg-ink-700 px-2.5 py-0.5 text-[12px] font-semibold text-amber-deep">{w.audience}</span>
                      <strong className="font-semibold">{w.label}</strong>
                      <span className="text-[13px] text-paper-dim">{w.detail}</span>
                    </div>
                  ))}
                </div>
              </section>

              <fieldset className="m-0 flex flex-col gap-2.5 border-0 p-0">
                <legend className="mb-2.5 p-0 text-[15px] font-semibold">When</legend>
                <label className={`${optBase} cursor-pointer ${effectiveWhen === 'now' ? optOn : 'border-ink-400'}`}>
                  <input type="radio" name="publish-when" className="mt-1" checked={effectiveWhen === 'now'} onChange={() => setWhen('now')} />
                  <span className="flex flex-col gap-0.5"><strong className="font-semibold">Now</strong>
                    <span className="text-[13px] text-paper-dim">It goes up in about a minute.</span></span>
                </label>
                <label className={`${optBase} ${slotReason ? 'cursor-not-allowed opacity-60 border-ink-400' : `cursor-pointer ${effectiveWhen === 'next_slot' ? optOn : 'border-ink-400'}`}`}>
                  <input type="radio" name="publish-when" className="mt-1" disabled={!!slotReason} checked={effectiveWhen === 'next_slot'}
                         aria-describedby={slotReason ? `${titleId}-slot` : undefined} onChange={() => setWhen('next_slot')} />
                  <span className="flex flex-col gap-0.5"><strong className="font-semibold">{slotOptionText(p.next_slot, p.releases_on, now)}</strong>
                    <span id={`${titleId}-slot`} className="text-[13px] text-paper-dim">
                      {slotReason ?? `Joins the schedule after ${p.queue_length} ${p.queue_length === 1 ? 'piece' : 'pieces'}.`}
                    </span></span>
                </label>
              </fieldset>

              {open > 0 && (
                <section className="flex flex-col gap-2 rounded-[10px] border border-amber-glow/50 bg-amber-glow/10 px-4 py-3.5">
                  <h3 className="m-0 text-[15px]">Still to look at</h3>
                  <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
                    {items.filter((i) => i.blocking).map((item) => (
                      <li key={item.key} className="flex items-center justify-between gap-3">
                        <span><strong className="font-semibold">{item.title}</strong>{item.detail && <span className="text-paper-dim"> {item.detail}</span>}</span>
                        {(item.claim || item.kind === 'search_title') && (
                          <button type="button" className={btnSmall} onClick={() => showItem(item)}>Show me</button>
                        )}
                      </li>
                    ))}
                  </ul>
                  <label className="mt-1 flex cursor-pointer items-center gap-2.5 font-semibold">
                    <input type="checkbox" className={`m-0 size-[18px] ${focus}`} checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />
                    I have checked these and want to publish anyway
                  </label>
                </section>
              )}
            </>
          )}
          {error && <p role="alert" className={errorText}>{error}</p>}
        </div>

        <div className="flex flex-wrap justify-end gap-2.5 border-t border-ink-500 px-7 pb-5 pt-4">
          <button type="button" className={btn} onClick={onClose}>Cancel</button>
          <button type="button" className={btnPrimary} disabled={!ready || send.isPending}
                  onClick={() => { setError(null); send.mutate() }}>
            {send.isPending ? 'Publishing…' : publishButtonLabel(effectiveWhen, p?.next_slot ?? null, now)}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
